import type {
  ColumnOrderState,
  ColumnPinningState,
} from '@tanstack/react-table';

import type { Column, Table } from '../../hooks/useTable/types.js';
import type { DataTableRowData } from '../../public.api.js';
import { getIsColumnPinned } from '../ColumnPinning/column-pinning-utils.js';

/**
 * Utility function to check if a column can be moved to the left.
 *
 * @param column - the column to be re-ordered
 * @param columnsToCompare - columns to compare against when re-ordering
 * @returns true if the column can be moved left, false if it's already at the leftmost position
 */
export function canMoveColumnLeft<TData extends DataTableRowData, TValue>(
  column: Column<TData, TValue>,
  columnsToCompare: Column<TData, unknown>[],
): boolean {
  let columnId: string | undefined = column.id;

  // For nested columns, we need to find the first leaf column
  // in the comparison list to determine if the entire group can move left
  if (column.columns.length > 0) {
    columnId = columnsToCompare.find(
      (leafColumn) =>
        leafColumn.id === column.id || leafColumn.parent?.id === column.id,
    )?.id;
  }

  // Column can move left if it's not already at the first position
  return columnsToCompare.at(0)?.id !== columnId;
}

/**
 * Utility function to check if a column can be moved to the right.
 *
 * @param column - the column to be re-ordered
 * @param columnsToCompare - columns to compare against when re-ordering
 * @returns true if the column can be moved right, false if it's already at the rightmost position
 */
export function canMoveColumnRight<TData extends DataTableRowData, TValue>(
  column: Column<TData, TValue>,
  columnsToCompare: Column<TData, unknown>[],
): boolean {
  let columnId: string | undefined = column.id;

  // For nested columns, we need to find the last leaf column
  // in the comparison list to determine if the entire group can move right
  if (column.columns.length > 0) {
    columnId = columnsToCompare.findLast(
      (leafColumn) =>
        leafColumn.id === column.id || leafColumn.parent?.id === column.id,
    )?.id;
  }

  // Column can move right if it's not already at the last position
  return columnsToCompare?.at(-1)?.id !== columnId;
}

/**
 * Returns the index of the given column in the provided column order.
 *
 * For nested column, returns the index of the *first* child column.
 */
function getFirstColumnIndex<TData, TValue>(
  column: Column<TData, TValue>,
  columnOrder: string[],
): number {
  if (column.columns.length) {
    // find index of first child in current column order
    return columnOrder.findIndex((columnId) =>
      column.columns.some((column) => column.id === columnId),
    );
  }
  // standalone column
  return columnOrder.indexOf(column.id);
}

/**
 * Returns the last index of the given column in the provided column order.
 *
 * For nested column, returns the index of the *last* child column.
 */
function getLastColumnIndex<TData, TValue>(
  column: Column<TData, TValue>,
  columnOrder: string[],
): number {
  if (column.columns.length) {
    // find index of last child in current column order
    return columnOrder.findLastIndex((columnId) =>
      column.columns.some((column) => column.id === columnId),
    );
  }
  // standalone column (for the standalone column the first index is also the last index)
  return columnOrder.indexOf(column.id);
}

/**
 * Finds a column by ID in the provided list of columns.
 */
function findColumnById<TData extends DataTableRowData, TValue>(
  columnId: string,
  columns: Column<TData, TValue>[],
): Column<TData, TValue> | undefined {
  return columns.find((c) => c.id === columnId);
}

/**
 * Returns the number of columns to move based on whether the column has child columns.
 */
function getColumnsToMoveCount<TData, TValue>(
  column: Column<TData, TValue>,
): number {
  return (
    // for nested columns, move all child columns together
    (column.columns.length || // for standalone columns, move only itself
    1)
  );
}

/**
 * Returns true, if the given column is pinned.
 * For nested column, returns true if all children are pinned or hidden.
 */
function isColumnFullyPinnedOrHidden<TData>(
  column: Column<TData, unknown> | undefined,
  columnsPinned: ColumnPinningState,
): boolean {
  if (!column) {
    return false;
  }
  if (!column.parent?.columns.length) {
    return (
      getIsColumnPinned(column.id, columnsPinned) || !column.getIsVisible()
    );
  }

  // nested column is only an invalid target, if all its siblings are pinned
  return column?.parent.columns.every(
    (col) => getIsColumnPinned(col.id, columnsPinned) || !col.getIsVisible(),
  );
}

/**
 * Retrieves new column order for moving a column left or right.
 *
 * @param currentColumnOrder - current column order
 * @param startIndex - current column index where the move starts
 * @param targetIndex - target index where the moved columns should be inserted
 * @param moveCount - how many columns to move based on column children
 */
const getNewColumnOrder = (
  currentColumnOrder: string[],
  startIndex: number,
  targetIndex: number,
  moveCount: number = 1,
): string[] => {
  // Extract the column(s) that should be moved
  const newColumnOrder = [...currentColumnOrder];
  const columnsToMove = newColumnOrder.splice(startIndex, moveCount);
  // Insert the column(s) at the new position
  newColumnOrder.splice(targetIndex, 0, ...columnsToMove);

  return newColumnOrder;
};

/**
 * Gets the relevant columns to compare against when moving a column.
 */
export function getColumnsToCompareForMove<TData, TValue>(
  table: Table<TData>,
  column: Column<TData, TValue>,
): Column<TData, unknown>[] {
  if (column.columns.length > 0 || !column.parent) {
    // For parent columns or standalone columns, use all center visible leaf columns
    return table.getCenterVisibleLeafColumnsWithoutBuiltIn();
  }
  // For child columns, use only their siblings (= visible leaf columns within the parent)
  return column.parent.getCenterVisibleLeafColumns();
}

/**
 * Moves a nested child column one position to the left within its parent group.
 * Skips pinned and hidden columns when determining the target position.
 */
function moveNestedColumnLeft<TData, TValue>(
  column: Column<TData, TValue>,
  leafColumns: Column<TData, unknown>[],
  columnOrder: string[],
  columnsPinned: ColumnPinningState,
): string[] {
  const columnIndex = columnOrder.indexOf(column.id);
  let targetIndex = columnIndex - 1;

  while (targetIndex >= 0) {
    const targetColumnId = columnOrder[targetIndex];
    const targetColumn = leafColumns.find((col) => col.id === targetColumnId);

    if (
      targetColumn &&
      !getIsColumnPinned(targetColumn.id, columnsPinned) &&
      targetColumn.getIsVisible()
    ) {
      // Found a valid target position
      break;
    }

    // skip pinned and hidden columns
    targetIndex--;
  }

  return getNewColumnOrder(columnOrder, columnIndex, targetIndex);
}

/**
 * Moves the current column one position to the left and updates the table state.
 *
 * Depending on whether the column is a nested column or a standalone column,
 * whether its neighboring columns are pinned, hidden, or nested,
 * we need to consider different scenarios to calculate the new column order correctly.
 *
 * CASE1: Column itself is a nested child column.
 *  - Constraint1: Can only move within the parent group.
 *  - Constraint2: Skip pinned siblings.
 *  - Constraint3: Skip hidden siblings.
 *
 * CASE2: Column is a standalone column or a parent column.
 *  - Constraint1: Skip entire nested columns as a whole.
 *  - Constraint2: Skip pinned columns.
 *  - Constraint3: Skip hidden columns.
 */
export function moveColumnLeft<TData, TValue>(
  column: Column<TData, TValue>,
  currentColumnOrder: ColumnOrderState,
  columnsPinned: ColumnPinningState,
  leafColumns: Column<TData, unknown>[],
): string[] {
  // CASE1: Column itself is a nested child column.
  if (column.parent) {
    const newColumnOrder = moveNestedColumnLeft(
      column,
      leafColumns,
      currentColumnOrder,
      columnsPinned,
    );
    return newColumnOrder;
  }

  // CASE2: Column is a standalone column or a parent column.
  const columnIndex = getFirstColumnIndex(column, currentColumnOrder);

  // target is one position to the left
  let currentTargetIndex = columnIndex - 1;

  // we can't move past the first position
  while (currentTargetIndex > 0) {
    const currentTargetId = currentColumnOrder[currentTargetIndex];
    const currentTargetColumn = findColumnById(currentTargetId, leafColumns);

    // Constraint1: Skip nested columns as a whole.
    if (currentTargetColumn?.parent) {
      currentTargetIndex -= currentTargetColumn.parent.columns.length - 1;
    }

    // Constraint2: Skip pinned or hidden columns.
    // For nested columns, skip if *entire group* is pinned or hidden.
    if (isColumnFullyPinnedOrHidden(currentTargetColumn, columnsPinned)) {
      currentTargetIndex--;
      continue;
    }

    break; // found valid target
  }

  const columnsToMove = getColumnsToMoveCount(column);
  const newColumnOrder = getNewColumnOrder(
    currentColumnOrder,
    columnIndex,
    currentTargetIndex,
    columnsToMove,
  );

  return newColumnOrder;
}

/**
 * Moves a nested child column one position to the right within its parent group.
 * Skips pinned and hidden columns when determining the target position.
 */
function moveNestedColumnRight<TData, TValue>(
  column: Column<TData, TValue>,
  leafColumns: Column<TData, unknown>[],
  columnOrder: ColumnOrderState,
  columnsPinned: ColumnPinningState,
): string[] {
  const columnIndex = columnOrder.indexOf(column.id);
  let targetIndex = columnIndex + 1;

  while (targetIndex < columnOrder.length) {
    const targetColumnId = columnOrder[targetIndex];
    const targetColumn = leafColumns.find((col) => col.id === targetColumnId);

    if (
      targetColumn &&
      !getIsColumnPinned(targetColumn.id, columnsPinned) &&
      targetColumn.getIsVisible()
    ) {
      // Found a valid target position
      break;
    }

    // skip pinned and hidden columns
    targetIndex++;
  }

  return getNewColumnOrder(columnOrder, columnIndex, targetIndex);
}

/**
 * Moves the current column one position to the right and returns the new column order.
 *
 * Depending on whether the column is a nested column or a standalone column,
 * whether its neighboring columns are pinned, hidden, or nested,
 * we need to consider different scenarios to calculate the new column order correctly.
 *a
 * CASE1: Column itself is a nested child column.
 *  - Constraint1: Can only move within the parent group.
 *  - Constraint2: Skip pinned siblings.
 *  - Constraint3: Skip hidden siblings.
 *
 * CASE2: Column is a standalone column or a parent column.
 *  - Constraint1: Skip entire nested columns as a whole.
 *  - Constraint2: Skip pinned columns.
 *  - Constraint3: Skip hidden columns.
 */
export function moveColumnRight<TData, TValue>(
  column: Column<TData, TValue>,
  currentColumnOrder: ColumnOrderState,
  columnsPinned: ColumnPinningState,
  leafColumns: Column<TData, unknown>[],
): string[] {
  // CASE1: Column itself is a nested child column.
  if (column.parent) {
    const newColumnOrder = moveNestedColumnRight(
      column,
      leafColumns,
      currentColumnOrder,
      columnsPinned,
    );

    return newColumnOrder;
  }

  // CASE2: Column is a standalone column or a parent column.
  const columnIndex = getLastColumnIndex(column, currentColumnOrder);

  // target is one position to the right
  let currentTargetIndex = columnIndex + 1;

  // we can't move past the last position
  while (currentTargetIndex < currentColumnOrder.length - 1) {
    const currentTargetId = currentColumnOrder[currentTargetIndex];
    const currentTargetColumn = findColumnById(currentTargetId, leafColumns);

    // Constraint1: Skip nested columns as a whole.
    if (currentTargetColumn?.parent) {
      currentTargetIndex += currentTargetColumn.parent.columns.length - 1;
    }

    // Constraint2: Skip pinned or hiddden columns.
    // For nested columns, skip if *entire group* is pinned or hidden.
    if (isColumnFullyPinnedOrHidden(currentTargetColumn, columnsPinned)) {
      // skip pinned columns
      currentTargetIndex++;
      continue;
    }

    break; // found valid target
  }

  const hasChildren = column.columns.length > 0;
  const startIndex = hasChildren
    ? // We used the last child index as start index,
      // so we need to adjust it to the first child index here.
      columnIndex - (column.columns.length - 1)
    : columnIndex;
  const targetIndex = hasChildren
    ? // We used the last child to calculate the target index,
      // so we need to adjust it here.
      currentTargetIndex - (column.columns.length - 1)
    : currentTargetIndex;
  const columnsToMove = getColumnsToMoveCount(column);
  const newColumnOrder = getNewColumnOrder(
    currentColumnOrder,
    startIndex,
    targetIndex,
    columnsToMove,
  );

  return newColumnOrder;
}
